<script setup>
import { ref, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();

// --- CONFIG ---
const API = "/client-project/wp-json/wp/v2";

// --- Reactive Data ---
const showPdf = ref(false);
const togglePdf = () => (showPdf.value = !showPdf.value);

const categories = ref([]);
const activeIndex = ref(null);
const partners = ref([]);
const events = ref([]);

const toggle = (index) => {
  activeIndex.value = activeIndex.value === index ? null : index;
};

// --- Utility ---
const extractImgSrc = (imgHtml) => {
  if (!imgHtml) return "";
  const match = imgHtml.match(/src="(.*?)"/);
  return match ? match[1].replace(/-\d+x\d+/, "") : "";
};

// --- Fetch Functions ---
const fetchCategories = async () => {
  try {
    const res = await fetch(
      `${API}/catering-service?acf_format=standard`
    );
    const data = await res.json();

    categories.value = data.map((item) => ({
      id: item.id,
      title:
        item.acf?.[`service_name_${locale.value}`]?.simple_value_formatted ||
        item.title?.rendered ||
        "Sans titre",
      description:
        item.acf?.[`description_${locale.value}`]?.simple_value_formatted ||
        "Aucune description disponible.",
    }));
  } catch (err) {
    console.error("Erreur de chargement services :", err);
  }
};

const fetchPartners = async () => {
  try {
    const res = await fetch(
      `${API}/partner?acf_format=standard`
    );
    const data = await res.json();

    partners.value = data.map((item) => ({
      id: item.id,
      title: item.title.rendered,
      image: extractImgSrc(item.acf?.image?.simple_value_formatted),
    }));
  } catch (err) {
    console.error("Erreur de chargement partenaires :", err);
  }
};

const fetchEvents = async () => {
  try {
    const res = await fetch(
      `${API}/cateringcategory`
    );
    const data = await res.json();

    events.value = data.map((item) => ({
      id: item.id,
      title:
        item.acf?.[`eventtype${locale.value}`]?.simple_value_formatted ||
        item.title.rendered,
      description:
        item.acf?.[`eventdescription${locale.value}`]?.simple_value_formatted ||
        "Aucune description disponible.",
      image: extractImgSrc(item.acf?.eventimage?.simple_value_formatted),
    }));
  } catch (err) {
    console.error("Erreur de chargement events :", err);
  }
};

// --- Initial Fetch ---
onMounted(() => {
  fetchCategories();
  fetchPartners();
  fetchEvents();
});

// --- Refetch when language changes ---
watch(locale, () => {
  fetchCategories();
  fetchEvents();
});
</script>

<template>
  <!-- Hero Section -->
<section class="hero">
  <div class="hero-overlay"></div>
  <img src="../assets/img/cateringbg.jpg" alt="Always Hungry background" class="hero-bg" />


<h1 class="hero-title">
  <span class="bold-text">{{ t("cateringTitle") }}</span>
</h1>

<a href="#catering-form" class="reserve-btn">
  {{ t("reserv") }}
</a>

</section>

  <!-- Banner -->
  <section class="banner">
    <div class="banner-content">
      <h2>{{ t("cateringBannerTitle") }}</h2>

<p>{{ t("cateringBannerText") }}</p>



  <div class="partners">
  <img
    v-for="partner in partners"
    :key="partner.id"
    :src="partner.image"
   
    class="partner-logo"
  />
</div>
    </div>
  </section>
   <div class="pdf-card">
  <h3>{{ t("pdfTitle") }}</h3>
  <p>{{ t("pdfText") }}</p>
  <a href="/CateringMenu.pdf" target="_blank" class="pdf-link">
    {{ t("openPdf") }}
  </a>
</div>
 <!-- Services -->
  <section class="header">
   <h2>{{ t("ourServices") }}</h2>
    <hr />
  </section>

 <section class="menu-accordion">
  <div v-for="(category, index) in categories" :key="category.id" class="accordion-item">
    <button class="accordion-header" @click="toggle(index)">
      {{ category.title }}
      <i class="fa" :class="activeIndex === index ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
    </button><transition name="accordion">
  <div v-if="activeIndex === index" class="accordion-content">
    <p>{{ category.description }}</p>
  </div>
</transition>
    
  </div>
</section>

  <!-- Events -->
 <section class="events-section">
  <div 
    v-for="(event, index) in events" 
    :key="event.id" 
    :class="['event', index % 2 === 1 ? 'reverse' : '']"
  >
    <img :src="event.image" :alt="event.title" class="event-image" />
    <div class="event-text">
      <h2>{{ event.title }}</h2>
      <p>{{ event.description }}</p>
    </div>
  </div>
</section>

 
  
<section class="header">
  <h2>{{ t('reserv') }}</h2>
  <hr />
</section>

<section id="catering-form" class="catering-form">
  <form
    class="form"
    action="https://formspree.io/f/meeejvjy"
    method="POST"
  >
    <div class="form-row">
      <div class="form-group">

        <label for="firstName">{{ t('firstName') }}</label>
        <input
          type="text"
          id="firstName"
          name="firstName"
          :placeholder="t('firstNamePlaceholder')"
          required
        />

        <label for="lastName">{{ t('lastName') }}</label>
        <input
          type="text"
          id="lastName"
          name="lastName"
          :placeholder="t('lastNamePlaceholder')"
          required
        />

        <label for="email">{{ t('email') }}</label>
        <input
          type="email"
          id="email"
          name="email"
          :placeholder="t('emailPlaceholder')"
          required
        />

        <label for="service">{{ t('serviceType') }}</label>
        <select id="service" name="service" required>
          <option value="">{{ t('select') }}</option>
          <option value="wedding">{{ t('wedding') }}</option>
          <option value="corporate">{{ t('corporate') }}</option>
          <option value="private">{{ t('private') }}</option>
        </select>

        <label for="guests">{{ t('guests') }}</label>
        <input
          type="number"
          id="guests"
          name="guests"
          :placeholder="t('guestsPlaceholder')"
          min="1"
          required
        />

        <label for="description">{{ t('description') }}</label>
        <textarea
          id="description"
          name="message"
          rows="4"
          :placeholder="t('descriptionPlaceholder')"
          required
        ></textarea>

        <button type="submit" class="submit-btn">
          {{ t("submit") }}
        </button>

      </div>
    </div>
  </form>
</section>


</template>

<style>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300..700;1,300..700&family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap');
@import url('https://fonts.googleapis.com/css2?family=League+Gothic&display=swap');
</style>

<style scoped>
html {
  scroll-behavior: smooth;
}
body {
  font-family: 'Montserrat', sans-serif;
  font-family: 'League Gothic', sans-serif;
  font-family: 'Cormorant Garamond', sans-serif;
}

/* HERO */

 .hero {
  position: relative;
  width: 100%;
  height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-top: 5rem;
}

.hero-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(75%);
  z-index: 0;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 1;
}

.reserve-btn {
  position: absolute;
  bottom: 4rem;
  right: 2rem;
  background-color: #dd7272;
  color: white;
  text-transform: uppercase;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 1.1rem;
  padding: 0.8rem 2rem;
  border-radius: 3rem;
  text-decoration: none;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  transition: all 0.3s ease;
  z-index: 10;
}

.reserve-btn:hover {
  background-color: #c55c5c;
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.3);
}



.hero h1 {
  position: absolute;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.5rem;
  font-size: 4rem;
  text-align: center;
  color: #dd7272;
  z-index: 10;
}


.bold-text {
  font-family: 'League Gothic', sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: 4rem;
  color: white;
  
}

/* BANNER */
.banner {
  background-color: #283A54;
  padding: 40px 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.banner-content {
  color: white;
  text-align: center;
  border: 2px solid white;
  padding: 2rem 7rem;
}
.banner-content p {
  font-size: 1.2rem;
  font-weight: 100;
  font-style: italic;
  font-family: 'Cormorant Garamond', sans-serif;
}
.banner-content h2 {
  font-size: 1.8rem;
  margin-top: 0;
  font-weight: 500;
  font-style: italic;
  font-family: 'Cormorant Garamond', sans-serif;
}
.partners-logos {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3rem;
  flex-wrap: wrap;
}

.partners {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
  margin-top: 2rem;
}

.partner-logo {
  max-height: 60px;
  width: auto;
  object-fit: contain;
  filter: brightness(0) invert(1); /* if you want white logos */
}
.partner-logo:hover {
  opacity: 1;
}
/* HEADERS */
.header h2 {
  font-family: 'Montserrat', sans-serif;
  font-weight: 300;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 0.5rem;
  margin-top: 4rem;
}
.header hr {
  border: 0.1mm solid #39383D;
  width: 25rem;
}

/* EVENTS */
.events-section {
  display: grid;
  gap: 4rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 1.5rem;
  font-family: "Cormorant Garamond", serif;
}
.event {
  display: grid;
  grid-template-columns: 1.3fr 1.3fr;
  align-items: center;
  gap: 10rem;
}
.event.reverse {
  grid-template-columns: 1.3fr 1.3fr;
}
.event.reverse .event-image {
  order: 2;
}
.event-image {
  width: 100%;
  aspect-ratio: 1 / 1; 
  object-fit: cover;   
 
}
.event-text h2 {
  font-size: 2.2rem;
  font-family: "Montserrat", sans-serif;
  margin-bottom: 1rem;
  letter-spacing: 1px;
  font-weight: 300;
  text-align: center;
}
.event-text p {
  font-size: 1.2rem;
  line-height: 1.6;
  color: #333;
  margin-bottom: 1rem;

}
@media (max-width: 850px) {
  .event {
    grid-template-columns: 1fr;
    text-align: center;
  }
  .event.reverse .event-image {
    order: 0;
  }
  
}


/* ACCORDION */
.menu-accordion {
  max-width: 1100px;
  margin: 4rem auto;
  padding: 0 1rem;
  border-top: 1px solid #b1b1b1;
  font-family: "Cormorant Garamond", serif;
}
.accordion-item {
  border-bottom: 1px solid #b1b1b1;
}

/* The visible service title */
.accordion-header {
  width: 100%;
  background: none;
  border: none;
  color: #000;
  font-size: 1.4rem;
  font-weight: 300;
  padding: 1.5rem 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  text-align: left;
  font-family: "Montserrat", sans-serif;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.accordion-header:hover {
  color: #283A54;
  transition: color 0.3s ease;
}

/* The blue dropdown content */
.accordion-content {
  background-color: #1d2a3b;
  color: white;
  padding: 1.5rem;
  font-size: 1.2rem;
  line-height: 1.7;
  animation: fadeIn 0.3s ease;
}
.accordion-content p,
.accordion-content div {
  margin: 0;
  font-family: "Cormorant Garamond", serif;
  font-size: 1.1rem;
  font-weight: 400;
}
.fa-chevron-down,
.fa-chevron-up {
  transition: transform 0.3s ease;
  font-size: 1rem;
}

/* Smooth dropdown animation */
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s ease;
}
.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
  padding: 0 1.5rem;
}
.accordion-enter-to,
.accordion-leave-from {
  max-height: 200px;
  opacity: 1;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
/*BUTTON MENU*/
.pdf-card {
  text-align: center;
  border: 1px solid #ccc;
  padding: 2rem;
  border-radius: 12px;
  max-width: 500px;
  margin: 3rem auto;
  font-family: "Cormorant Garamond", serif;
  font-size: 1.4rem;
}
.pdf-link {
  display: inline-block;
  margin-top: 1rem;
  padding: 0.5rem 1.7rem;
  background-color: #283A54;
  color: white;
  text-decoration: none;
  font-family: "Montserrat", sans-serif;
  text-transform: uppercase;
 
  transition: background 0.3s ease;
}
.pdf-link:hover {
  background-color: #111;
}


.catering-form {
  max-width: 900px;
  margin: 4rem auto;
  font-family: "Cormorant Garamond", serif;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
}

.form-row {
  display: flex;
  gap: 2rem;
}

.form-group {
  flex: 1;
  display: flex;
  flex-direction: column;
}

label {
  font-size: 1.2rem;
  margin-bottom: 0.3rem;
  font-family: "montserrat", sans-serif;
}

input,
select,
textarea {
  border: 1px solid #444;
  padding: 0.7rem;
  font-size: 1rem;
  font-family: "Montserrat", sans-serif;
  outline: none;
  transition: border 0.2s ease;
  margin: 5px;
}

input:focus,
select:focus,
textarea:focus {
  border-color: #000;
}

.full {
  width: 100%;
}

.submit-btn {
  align-self: center;
  background: #283A54;
  color: white;
  border: none;
  padding: 0.9rem 3rem;
  letter-spacing: 0.1rem;
  font-family: "Montserrat", sans-serif;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background 0.3s ease;
  margin: 5px;
}

.submit-btn:hover {
  background: #111;
}



/* RESPONSIVE ADJUSTMENTS */

/* ---------- HERO ---------- */
@media (max-width: 1024px) {
  .hero h1 {
    font-size: 3rem;
    flex-wrap: wrap;
    text-align: center;
    padding: 0 1rem;
  }

  .bold-text {
    font-size: 4rem;
  }

  .italic-text {
    font-size: 3rem;
  }
    .bold-text {
    font-size: 4.5rem;
  }

  h4 {
    font-size: 4rem;
    margin: 1.5rem;
  }

  .newsletter-form {
    width: 80%;
  }
   .banner-content {
    padding: 2rem 3rem;
  }

  .event {
    gap: 4rem;
  }

  .form-row {
    gap: 1.2rem;
  }
  .partner-logo {
    max-height: 70px;
  }
  .header hr {
    width: 15rem;
  }
  .banner-content h2 {
    font-size: 1.5rem;
  }
   .banner-content p {
    font-size: 0.9rem;
  }





@media (max-width: 600px) {
  

  .hero h1 {
    font-size: 2rem;
    flex-direction: column;
    gap: 0.3rem;
  }

  .bold-text {
    font-size: 3rem;
  }

  .italic-text {
    font-size: 2rem;
        font-weight: 600;
        margin: auto;
  }
   .partner-logo {
    max-height: 30px;
  }
  .reserve-btn {
    padding: 0.6rem 1.5rem;
    font-size: 0.9rem;
    bottom: 2rem;
    right: 1rem;
  }
}


@media (max-width: 768px) {

  .hero{
    height: 80vh;
  }
     
  .hero h1 {
    flex-direction: column;
    font-size: 3.5rem;
    gap: 0.3rem;
  }

  h4 {
    font-size: 3rem;
  }
  .event-image {
  width: 100%;
  aspect-ratio: 1 / 1; 
  object-fit: cover;   
 
}

  .newsletter {
    margin: 3rem auto;
  }

  .newsletter h2 {
    font-size: 1.5rem;
  }

  .newsletter-form {
    flex-direction: column;
    width: 90%;
    box-shadow: none;
    border: none;
  }

  .newsletter-input {
    width: 100%;
    border: 1px solid #333;
    margin-bottom: 1rem;
  }

  .newsletter-btn {
    border: 1px solid #333;
    width: 100%;
    padding: 0.8rem;
  }
 
}


@media (max-width: 480px) {
  .hero h1 {
    font-size: 3.5rem;
  }

  .bold-text {
    font-size: 3.5rem;
  }

  h4 {
    font-size: 2rem;
    margin: 1rem;
  }

  .newsletter h2 {
    font-size: 1.2rem;
  }

  .newsletter-input {
    font-size: 0.9rem;
  }

  .newsletter-btn {
    font-size: 1rem;
  }
}


/* Mobile Landscape / Small Tablets */
@media (max-width: 768px) {
  .hero {
    height: 80vh;
  }
  .banner {
    padding: 2rem 1rem;
  }

  .banner-content {
    padding: 1.5rem;
    width: 90%;
  }

  .banner-content p {
    font-size: 1rem;
  }

  .event {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .event-text h2 {
    font-size: 1.8rem;
  }

  .event-text p {
    font-size: 1rem;
  }
}

@media (max-width: 850px) {
  .events-section {
    display: flex;
    flex-direction: column;
    gap: 3rem;
  }


  .event {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1.5rem;
  }


  .event.reverse {
    flex-direction: column;
  }

  .event-image {
    width: 100%;
    max-width: 600px;
    aspect-ratio: 1 / 1;
    object-fit: cover;
  }

  .menu-accordion {
    width: 100%;
    padding: 0 0.5rem;
  }

  .accordion-header {
    font-size: 1.1rem;
  }

  .accordion-content {
    font-size: 0.95rem;
  }

  .pdf-card {
    width: 90%;
    padding: 1.5rem;
  }

  .catering-form {
    padding: 0 1rem;
  }

  .form-row {
    flex-direction: column;
    gap: 1rem;
  }

  .submit-btn {
    width: 100%;
    padding: 1rem;
    text-transform: capitalize;
  }
}


@media (max-width: 480px) {
  .hero h1 {
    font-size: 4rem;
  }

  

  .banner-content {
    padding: 1rem;
  }

  .event-text h2 {
    font-size: 1.4rem;
  }

  .event-text p {
    font-size: 1rem;
    
  }

  .accordion-header {
    font-size: 0.9rem;
    padding: 1rem 0;
  }

  .accordion-content {
    font-size: 0.9rem;
  }
  .accordion-content p{
    font-size: 1rem;
  }

  .pdf-card h3 {
    font-size: 1.1rem;
  }

  .pdf-card p {
    font-size: 0.9rem;
  }

  .form-group label {
    font-size: 1rem;
  }

  input,
  select,
  textarea {
    font-size: 0.9rem;
  }

  .submit-btn {
    font-size: 1rem;
  }
}
}
</style>